import Swal from 'sweetalert2';
import { axiosClient } from './axiosClient';

export type AlumniInstituteOption = {
    instituteId: number | string;
    instituteName?: string;
};

const escapeHtml = (value: string) =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

const fetchAlumniInstitutes = async (uuid: string): Promise<AlumniInstituteOption[]> => {
    const response = await axiosClient().get(
        `${process.env.REACT_APP_STUDENT_MANAGEMENT_SERVER}/school-directory/user-institutes/${uuid}`
    );
    return response.data?.data || [];
};

export const confirmDeleteAlumniInstitute = async (
    uuid: string,
    preselectedInstituteId?: string | number | null
): Promise<{ uuid: string; instituteId: number } | null> => {
    if (!uuid) return null;

    Swal.fire({
        title: 'Loading institutes...',
        allowOutsideClick: false,
        didOpen: () => {
            Swal.showLoading();
        },
    });

    let institutes: AlumniInstituteOption[] = [];
    try {
        institutes = await fetchAlumniInstitutes(uuid);
    } catch (error) {
        await Swal.fire({
            icon: 'error',
            title: 'Unable to load institutes',
            text: 'Please try again.',
        });
        return null;
    }

    if (!institutes.length) {
        await Swal.fire({
            icon: 'info',
            title: 'No institute mapping',
            text: 'This user has no active institute mapping to delete.',
        });
        return null;
    }

    // Active mappings only (is_deleted != 1). Already-deleted institutes are omitted, so one
    // leftover mapping is treated as a single-institute case (readonly dropdown).
    const isSingle = institutes.length === 1;
    const preselect = String(
        institutes.some((institute) => String(institute.instituteId) === String(preselectedInstituteId))
            ? preselectedInstituteId
            : institutes[0].instituteId
    );

    const options = institutes
        .map((institute) => {
            const selected = String(institute.instituteId) === preselect ? 'selected' : '';
            const label = institute.instituteName || String(institute.instituteId);
            return `<option value="${escapeHtml(String(institute.instituteId))}" ${selected}>${escapeHtml(label)}</option>`;
        })
        .join('');

    const result = await Swal.fire({
        title: 'Delete from institute?',
        html: `
            <p class="text-start mb-2">The alumni will be removed from the selected institute only.</p>
            <label class="form-label text-start d-block" for="swal-delete-institute">Institute</label>
            <select id="swal-delete-institute" class="form-select" ${isSingle ? 'disabled' : ''}>${options}</select>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, delete',
        cancelButtonText: 'Cancel',
        focusConfirm: false,
        preConfirm: () => {
            const select = document.getElementById('swal-delete-institute') as HTMLSelectElement | null;
            const value = select?.value;
            if (!value) {
                Swal.showValidationMessage('Please select an institute');
                return false;
            }
            return value;
        },
    });

    if (!result.isConfirmed || !result.value) return null;
    return { uuid, instituteId: Number(result.value) };
};
